import React, { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { ErrorBoundary, ToastContainer } from '@/components/common';
import { AppProviders } from '@/store';
import { useReduxAuth } from '@/hooks/useReduxAuth';
import { SuperadminAuthProvider } from '@/superadmin/contexts/SuperadminAuthContext';
import { theme, GlobalStyles } from '@/styles';
import { router } from '@/routes/AppRouter';
import { setupInterceptors } from '@/api';

// App component without providers
const AppContent: React.FC = () => {
  const { restoreAuth } = useReduxAuth();

  useEffect(() => {
    // Setup API interceptors
    setupInterceptors();

    // Restore authentication state from Redux
    console.log('App: Attempting to restore auth...');
    restoreAuth().then(() => {
      console.log('App: Auth restoration completed');
    }).catch((error) => {
      console.log('App: Auth restoration failed:', error);
    });
  }, [restoreAuth]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={theme => ({
          '*': {
            margin: 0,
            padding: 0,
            boxSizing: 'border-box',
          },
          html: {
            height: '100%',
            WebkitOverflowScrolling: 'touch',
          },
          body: {
            height: '100%',
            fontFamily: theme.typography.fontFamily,
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            lineHeight: 1.5,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '#root': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        })}
      />
      <ErrorBoundary>
        <RouterProvider router={router} />
        <ToastContainer />
      </ErrorBoundary>
    </ThemeProvider>
  );
};

// Main App component with providers
const App: React.FC = () => {
  return (
    <React.StrictMode>
      <AppProviders>
        <SuperadminAuthProvider>
          <AppContent />
        </SuperadminAuthProvider>
      </AppProviders>
    </React.StrictMode>
  );
};

export default App;
